import { useEffect, useState } from 'react';
import { api, type Adapter, type WifiStatus } from '../lib/api.ts';
import { describeTransportSupport, type TransportKind } from '../lib/transports/types.ts';
import { AdapterForm } from './AdapterForm.tsx';
import { transportLabel, WifiTools } from './AdapterTools.tsx';
import { Modal } from './Modal.tsx';

interface Props {
  adapters: Adapter[];
  company: string | null;
  /** Owners and technicians can add adapters; a viewer can only pick one. */
  canWrite: boolean;
  onAdaptersChanged: () => void;
  /** The adapter this person will scan with. The parent saves it as theirs. */
  onChosen: (adapter: Adapter) => void;
  /** Only offered to someone who cannot finish setup on their own. */
  onClose?: () => void;
}

const CHOICES: { kind: Exclude<TransportKind, 'sim'>; title: string; examples: string }[] = [
  { kind: 'ble', title: 'Bluetooth reader', examples: 'OBDLink CX, Vgate, vLinker and most clones' },
  { kind: 'serial', title: 'USB cable or paired Bluetooth', examples: 'OBDLink SX, EX, or an MX paired in system settings' },
  { kind: 'wifi', title: 'Wi-Fi reader', examples: 'OBDLink MX Wi-Fi and Wi-Fi ELM327 readers' },
];

/**
 * The question nothing used to ask: how does this person reach the car?
 *
 * Every company was handed one placeholder per transport, so the picker always
 * had something selected and a new user had no reason to think a reader needed
 * setting up until a scan failed. This shows once, to anyone who can scan and
 * has not chosen an adapter, and again if they press Scan without one.
 */
export function AdapterSetup({ adapters, company, canWrite, onAdaptersChanged, onChosen, onClose }: Props) {
  const [step, setStep] = useState<'choose' | TransportKind | 'wifi-test'>('choose');
  const [savedWifiId, setSavedWifiId] = useState<number | null>(null);
  const [wifi, setWifi] = useState<WifiStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.wifiStatus().then(setWifi).catch(() => setWifi(null));
  }, []);

  const simulator = adapters.find((a) => a.transport === 'sim');
  const savedWifi = adapters.find((a) => a.id === savedWifiId);

  async function useSimulator() {
    setError(null);
    try {
      onChosen(simulator ?? (await api.saveAdapter({ name: 'Simulator', transport: 'sim' })));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function unavailable(kind: TransportKind): string | null {
    const support = describeTransportSupport(kind);
    if (!support.supported) return support.reason ?? 'Not available in this browser.';
    if (kind === 'wifi' && wifi?.remoteBridge) {
      return 'Wi-Fi readers only work when the bridge runs on your own machine, not on this hosted one.';
    }
    return null;
  }

  return (
    <Modal title="Set up your adapter" onClose={onClose}>
      {step === 'choose' && (
        <div className="stack">
          <p className="small" style={{ margin: 0 }}>
            Before you scan, tell us how this computer reaches the car. You can change it any time under Adapter
            settings.
          </p>

          {adapters.length > 0 && (
            <section className="stack" style={{ gap: 6 }}>
              <span className="eyebrow" style={{ margin: 0 }}>
                Already set up{company ? ` at ${company}` : ''}
              </span>
              {adapters.map((a) => (
                <button key={a.id} className="setup-existing" onClick={() => onChosen(a)}>
                  <strong>{a.name}</strong>
                  <span className="small dim">{transportLabel(a.transport)}</span>
                </button>
              ))}
            </section>
          )}

          {canWrite ? (
            <section className="stack" style={{ gap: 6 }}>
              <span className="eyebrow" style={{ margin: 0 }}>
                {adapters.length ? 'Or add a reader' : 'Add your reader'}
              </span>
              <div className="setup-choices">
                {CHOICES.map((c) => {
                  const reason = unavailable(c.kind);
                  return (
                    <button
                      key={c.kind}
                      className="setup-choice"
                      disabled={Boolean(reason)}
                      onClick={() => setStep(c.kind)}
                      title={reason ?? undefined}
                    >
                      <strong>{c.title}</strong>
                      <span className="small dim">{reason ?? c.examples}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : (
            adapters.length === 0 && (
              <div className="banner info small">
                Nobody at {company ?? 'your company'} has added an adapter yet. Ask an owner or technician to add one,
                then come back to Adapter settings.
              </div>
            )
          )}

          {(canWrite || simulator) && (
            <div className="setup-explore">
              <span className="small dim">No reader to hand?</span>
              <button className="tiny ghost" onClick={useSimulator}>
                Use the simulator
              </button>
            </div>
          )}

          {error && <div className="banner error small">{error}</div>}
        </div>
      )}

      {(step === 'ble' || step === 'serial' || step === 'wifi') && (
        <div className="stack">
          <span className="eyebrow" style={{ margin: 0 }}>
            {CHOICES.find((c) => c.kind === step)?.title}
          </span>
          <AdapterForm
            fixedTransport={step}
            submitLabel={step === 'wifi' ? 'Save and test' : 'Save and finish'}
            onCancel={() => setStep('choose')}
            onSaved={(adapter) => {
              onAdaptersChanged();
              if (adapter.transport === 'wifi') {
                // Tested before setup ends, so the first sign of a wrong address
                // is here and not a failed scan with a car waiting.
                setSavedWifiId(adapter.id);
                setStep('wifi-test');
              } else {
                onChosen(adapter);
              }
            }}
          />
        </div>
      )}

      {step === 'wifi-test' && savedWifi && (
        <div className="stack">
          <p className="small" style={{ margin: 0 }}>
            Saved <strong>{savedWifi.name}</strong>. Join its network and test it now, or finish and do it before your
            first scan.
          </p>
          <WifiTools adapter={savedWifi} onAdapterChanged={onAdaptersChanged} />
          <div className="row">
            <button className="primary" onClick={() => onChosen(savedWifi)}>
              Finish
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
